import { API_URL } from "../config";
import { fetchWithTransientRetry } from "./fetchWithTransientRetry";

const studentCourseTemarioCache = new Map();
const studentCourseTemarioInflight = new Map();
const studentCourseTestsCache = new Map();
const studentCourseTestsInflight = new Map();

function encodeCoursePathSegment(cursoId) {
  return encodeURIComponent(cursoId);
}

function isCurrentInflight(inflightMap, key, entry) {
  return inflightMap.get(key) === entry;
}

async function parseTemarioResponse(response) {
  if (response.status === 403) {
    const errBody = await response.json().catch(() => ({}));
    return {
      error: "no-autorizado",
      message: typeof errBody.error === "string" ? errBody.error : "Sin acceso al temario",
    };
  }

  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    return {
      error: "no-encontrado",
      message: typeof errBody.error === "string" ? errBody.error : "Curso no encontrado",
    };
  }

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    return {
      error: "error-servidor",
      message: typeof errBody.error === "string" ? errBody.error : "No se pudo cargar el temario",
    };
  }

  return response.json();
}

export async function getStudentCourseTemario(cursoId, options = {}) {
  const key = String(cursoId || "");
  if (!key) {
    return { error: "no-encontrado", message: "Curso no encontrado" };
  }
  if (options.force === true) {
    studentCourseTemarioCache.delete(key);
  } else if (studentCourseTemarioCache.has(key)) {
    return studentCourseTemarioCache.get(key);
  }
  if (studentCourseTemarioInflight.has(key)) {
    return studentCourseTemarioInflight.get(key).promise;
  }

  const entry = { promise: null };
  const encodedKey = encodeCoursePathSegment(key);
  const request = fetchWithTransientRetry(`${API_URL}/api/cursos/${encodedKey}/mi-temario`, {
    credentials: "include",
  })
    .then(parseTemarioResponse)
    .then((payload) => {
      if (!payload?.error && isCurrentInflight(studentCourseTemarioInflight, key, entry)) {
        studentCourseTemarioCache.set(key, payload);
      }
      return payload;
    })
    .catch((error) => ({
      error: "error-red",
      message: error instanceof Error ? error.message : "Error de conexión",
    }))
    .finally(() => {
      if (isCurrentInflight(studentCourseTemarioInflight, key, entry)) {
        studentCourseTemarioInflight.delete(key);
      }
    });

  entry.promise = request;
  studentCourseTemarioInflight.set(key, entry);
  return request;
}

export async function getStudentCourseTestsStructure(cursoId) {
  const key = String(cursoId || "");
  if (!key) {
    return { estructura: [] };
  }
  if (studentCourseTestsCache.has(key)) {
    return studentCourseTestsCache.get(key);
  }
  if (studentCourseTestsInflight.has(key)) {
    return studentCourseTestsInflight.get(key).promise;
  }

  const entry = { promise: null };
  const encodedKey = encodeCoursePathSegment(key);
  const request = fetch(`${API_URL}/api/cursos/${encodedKey}/tests/estructura`, {
    credentials: "include",
  })
    .then(async (response) => ({
      payload: response.ok ? await response.json() : { estructura: [] },
      cacheable: response.ok,
    }))
    .then(({ payload, cacheable }) => {
      const safePayload = {
        ...payload,
        estructura: Array.isArray(payload?.estructura) ? payload.estructura : [],
      };
      if (cacheable && isCurrentInflight(studentCourseTestsInflight, key, entry)) {
        studentCourseTestsCache.set(key, safePayload);
      }
      return safePayload;
    })
    .catch(() => ({ estructura: [] }))
    .finally(() => {
      if (isCurrentInflight(studentCourseTestsInflight, key, entry)) {
        studentCourseTestsInflight.delete(key);
      }
    });

  entry.promise = request;
  studentCourseTestsInflight.set(key, entry);
  return request;
}

export function clearStudentCourseDataCache() {
  studentCourseTemarioCache.clear();
  studentCourseTemarioInflight.clear();
  studentCourseTestsCache.clear();
  studentCourseTestsInflight.clear();
}
